/* =========================================================
   TR — Product Gallery Overlay Arrows (thumbnail-driven)
   Best for Dawn "thumbnail_slider" layout:
   - Overlay arrows click the next/prev THUMBNAIL button (source of truth)
   - Keeps selected thumbnail + rail movement perfectly in sync
========================================================= */

(() => {
  const DESKTOP_MQ = '(min-width: 750px)';
  const GALLERY_SEL = 'media-gallery[id^="MediaGallery-"]';
  const VIEWER_SEL  = 'slider-component[id^="GalleryViewer-"]';

  // Thumbnails (carousel)
  const THUMBS_WRAP_SEL = 'slider-component[id^="GalleryThumbnails-"]';
  const THUMB_LI_SEL    = 'li.thumbnail-list__item[data-target]';
  const THUMB_BTN_SEL   = 'button.thumbnail';
  const MEDIA_LIST_SEL  = 'ul.product__media-list';
  const ENHANCED_CLASS = 'tr-pg-enhanced';

  function isDesktop() {
    return window.matchMedia(DESKTOP_MQ).matches;
  }

  function getCaretMarkup(gallery) {
    return (
      gallery.querySelector('.slider-button .svg-wrapper')?.innerHTML ||
      '<svg viewBox="0 0 10 6" aria-hidden="true" focusable="false"><path d="M9.354.646a.5.5 0 0 0-.708 0L5 4.293 1.354.646a.5.5 0 1 0-.708.708l4 4a.5.5 0 0 0 .708 0l4-4a.5.5 0 0 0 0-.708Z"></path></svg>'
    );
  }

  function getThumbItems(gallery) {
    const thumbsWrap = gallery.querySelector(THUMBS_WRAP_SEL);
    if (!thumbsWrap) return { thumbsWrap: null, items: [] };

    const items = Array.from(thumbsWrap.querySelectorAll(THUMB_LI_SEL));
    return { thumbsWrap, items };
  }

  function getCurrentThumbIndex(gallery, items) {
    if (!items.length) return 0;

    const currentBtn = gallery.querySelector(`${THUMB_BTN_SEL}[aria-current="true"]`);
    const currentLi = currentBtn?.closest(THUMB_LI_SEL);
    if (!currentLi) return 0;

    const idx = items.indexOf(currentLi);
    return idx >= 0 ? idx : 0;
  }

  function clickThumbAt(items, idx) {
    const li = items[idx];
    const btn = li?.querySelector(THUMB_BTN_SEL);
    if (!btn) return false;

    // Clicking the thumbnail is what keeps Dawn fully in sync
    btn.click();

    // Keep it visible without forcing direction weirdness
    try {
      btn.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    } catch (_) {}

    return true;
  }

  function gotoDelta(gallery, delta) {
    const { items } = getThumbItems(gallery);
    if (items.length < 2) return;

    const current = getCurrentThumbIndex(gallery, items);
    const next = (current + delta + items.length) % items.length;

    // Some themes update aria-current asynchronously; small delay helps reliability
    // but we still click immediately.
    clickThumbAt(items, next);
  }

  function cleanupGallery(gallery) {
    if (typeof gallery.__trPgCleanup === 'function') {
      gallery.__trPgCleanup();
      gallery.__trPgCleanup = null;
    }
    gallery.classList.remove(ENHANCED_CLASS);
    gallery.classList.remove('tr-pg-dragging');
    gallery.querySelectorAll('.tr-pg-prev, .tr-pg-next').forEach((el) => el.remove());
  }

  function shouldIgnoreDragStart(target) {
    if (!target) return true;

    // Allow drag on Dawn's media click/zoom wrappers.
    if (target.closest('.product__modal-opener, .product__media-toggle, .product__media')) {
      return false;
    }

    // Ignore explicit controls and embedded interactive media.
    return Boolean(
      target.closest(
        '.tr-pg-prev, .tr-pg-next, .slider-button, a, button, input, select, textarea, summary, details, .deferred-media, .product__xr-button, model-viewer, video, iframe'
      )
    );
  }

  function enableDragNavigation(gallery, viewer) {
    let dragActive = false;
    let startX = 0;
    let startY = 0;
    let hasTriggered = false;
    let isHorizontalDrag = false;
    let blockClickUntil = 0;

    const detachWindowDragListeners = () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('blur', onMouseUp);
    };

    const resetDrag = () => {
      dragActive = false;
      isHorizontalDrag = false;
      hasTriggered = false;
      gallery.classList.remove('tr-pg-dragging');
      detachWindowDragListeners();
    };

    const onMouseDown = (event) => {
      if (!isDesktop()) return;
      if (event.button !== 0) return;
      if (shouldIgnoreDragStart(event.target)) return;

      const { items } = getThumbItems(gallery);
      if (items.length < 2) return;

      dragActive = true;
      startX = event.clientX;
      startY = event.clientY;
      hasTriggered = false;
      isHorizontalDrag = false;
      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);
      window.addEventListener('blur', onMouseUp);
    };

    const onMouseMove = (event) => {
      if (!dragActive) return;

      const dx = event.clientX - startX;
      const dy = event.clientY - startY;
      const absX = Math.abs(dx);
      const absY = Math.abs(dy);

      if (!isHorizontalDrag) {
        if (absX < 8 && absY < 8) return;
        if (absY > absX) {
          resetDrag();
          return;
        }
        isHorizontalDrag = true;
        gallery.classList.add('tr-pg-dragging');
      }

      if (hasTriggered) return;

      if (absX >= 36) {
        event.preventDefault();
        hasTriggered = true;
        blockClickUntil = Date.now() + 350;
        gotoDelta(gallery, dx > 0 ? -1 : 1);
      }
    };

    const onMouseUp = () => {
      if (!dragActive) return;
      resetDrag();
    };

    const onDragStart = (event) => {
      event.preventDefault();
    };

    const onClickCapture = (event) => {
      if (Date.now() > blockClickUntil) return;
      if (!event.target.closest('.product__modal-opener, .product__media-toggle, .product__media')) return;
      event.preventDefault();
      event.stopPropagation();
    };

    viewer.addEventListener('mousedown', onMouseDown);
    viewer.addEventListener('dragstart', onDragStart);
    viewer.addEventListener('click', onClickCapture, true);

    return () => {
      resetDrag();
      viewer.removeEventListener('mousedown', onMouseDown);
      viewer.removeEventListener('dragstart', onDragStart);
      viewer.removeEventListener('click', onClickCapture, true);
    };
  }

  function enhanceGallery(gallery) {
    cleanupGallery(gallery);

    if (!isDesktop()) return;

    const viewer = gallery.querySelector(VIEWER_SEL);
    if (!viewer) return;
    const mediaList = viewer.querySelector(MEDIA_LIST_SEL);
    if (!mediaList) return;

    const { items } = getThumbItems(gallery);

    // If no thumbnail carousel exists, we can’t do the “perfect sync” method.
    // (We can add a fallback later if you want.)
    if (items.length < 2) return;

    const caret = getCaretMarkup(gallery);

    const prev = document.createElement('button');
    prev.type = 'button';
    prev.className = 'tr-pg-prev';
    prev.setAttribute('aria-label', 'Previous image');
    prev.innerHTML = `<span class="svg-wrapper">${caret}</span>`;

    const next = document.createElement('button');
    next.type = 'button';
    next.className = 'tr-pg-next';
    next.setAttribute('aria-label', 'Next image');
    next.innerHTML = `<span class="svg-wrapper">${caret}</span>`;

    prev.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      gotoDelta(gallery, -1);
    });

    next.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      gotoDelta(gallery, +1);
    });

    viewer.appendChild(prev);
    viewer.appendChild(next);
    const dragCleanup = enableDragNavigation(gallery, viewer);
    gallery.__trPgCleanup = dragCleanup;
    gallery.classList.add(ENHANCED_CLASS);
  }

  function init() {
    document.querySelectorAll(GALLERY_SEL).forEach(enhanceGallery);
  }

  const scheduleInit = (() => {
    let rafId = 0;
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        init();
      });
    };
  })();

  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', scheduleInit);

  // Re-init after variant changes (Dawn swaps featured media + thumbnail state)
  document.addEventListener('variant:change', () => setTimeout(scheduleInit, 120));
  window.addEventListener('resize', scheduleInit);
})();
